import { useState } from 'react'
import { LayoutGrid, List } from 'lucide-react'

export type ListView = 'grid' | 'list'

// A page's card/list choice, remembered in localStorage under `key`. If the browser blocks
// storage, the choice just isn't remembered.
export function useStoredView(key: string): [ListView, (view: ListView) => void] {
  const [view, setView] = useState<ListView>(() => {
    try {
      return localStorage.getItem(key) === 'list' ? 'list' : 'grid'
    } catch {
      return 'grid'
    }
  })

  const changeView = (next: ListView) => {
    setView(next)
    try {
      localStorage.setItem(key, next)
    } catch {
      // Storage is blocked: keep the choice for this visit only.
    }
  }

  return [view, changeView]
}

const OPTIONS: { value: ListView; title: string; Icon: typeof List }[] = [
  { value: 'grid', title: 'Card view', Icon: LayoutGrid },
  { value: 'list', title: 'List view', Icon: List },
]

export function ViewToggle({ view, onChange }: { view: ListView; onChange: (view: ListView) => void }) {
  return (
    <div className="flex items-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-100 p-0.5">
      {OPTIONS.map(({ value, title, Icon }) => (
        <button
          key={value}
          onClick={() => onChange(value)}
          title={title}
          aria-pressed={view === value}
          className={`flex items-center justify-center h-9 w-9 rounded-md transition-colors ${
            view === value
              ? 'bg-brand-600 text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  )
}
